import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DateTime } from 'luxon';
import { ApexAxisChartSeries, ApexChart, ApexFill, ApexStroke, ApexYAxis, ApexXAxis, ApexTitleSubtitle, ApexTooltip } from 'ng-apexcharts';

interface AqiDataPoint {
  pm1: number;
  pm10: number;
  pm2_5: number;
  aqi: number;
  category?: string;
  range?: string;
  timestamp: number;
}

@Component({
  selector: 'app-aqi-graph',
  templateUrl: './aqi-graph.component.html',
})
export class AqiGraphComponent implements OnChanges {
  @Input() data!: AqiDataPoint[];

  series: ApexAxisChartSeries = [
    {
      name: "AQI",
      data: [],
      color: "#f59e42", // amber-500
    },
    {
      name: "PM2.5",
      data: [],
      color: "#06b6d4", // cyan-500
    },
    {
      name: "PM1.0",
      data: [],
      color: "#6366f1", // indigo-500
    },
    {
      name: "PM10",
      data: [],
      color: "#10b981", // emerald-500
    },
  ]
  chart: ApexChart = {
    height: 450,
    type: "line",
    stacked: false,
  }
  stroke: ApexStroke = {
    width: 3,
    curve: "smooth",
  }
  xaxis: ApexXAxis = {
    type: "datetime",
    labels: {
      datetimeUTC: false,
      format: 'MMM dd, yyyy HH:mm'
    },
  }
  fill: ApexFill = {
    type: "solid",
  }
  yaxis: ApexYAxis = {
    title: {
      text: "AQI/PM Reading"
    },
  }
  tooltip: ApexTooltip = {
    enabled: true,
    shared: true,
    intersect: false,
    x: {
      format: 'MMM dd, yyyy HH:mm',
      show: true,
    },
    y: {
      formatter: (val: number, opts?: any) => {
        if (!opts || !opts.series || typeof opts.seriesIndex === 'undefined') return val?.toLocaleString?.() ?? val;
        const seriesName = opts.w?.config?.series?.[opts.seriesIndex]?.name || '';
        if (seriesName === "AQI") {
          return `${val}`;
        }
        if (seriesName === "PM2.5" || seriesName === "PM10" || seriesName === "PM1.0") {
          return `${typeof val === "number" ? val.toFixed(2) : val} µg/m³`;
        }
        return typeof val === "number" ? val.toFixed(2) : val;
      }
    },
    marker: {
      show: true,
    },
    style: {
      fontSize: "14px",
      fontFamily: "inherit"
    }
  }

  constructor() {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.updateSeries();
    }
  }

  private updateSeries(): void {
    // Reset series data if data is empty or null
    if (!this.data || this.data.length === 0) {
      this.series[0].data = [];
      this.series[1].data = [];
      this.series[2].data = [];
      this.series[3].data = [];
      // Force chart update by creating new series array reference
      this.series = [...this.series];
      return;
    }

    // Update series with new data - create new array references to trigger change detection
    this.series = [
      {
        name: "AQI",
        data: this.data.map((item: AqiDataPoint): [number, number] => {
          return [DateTime.fromISO(item.timestamp.toString()).toMillis(), item.aqi];
        }) as any,
        color: "#f59e42",
      },
      {
        name: "PM2.5",
        data: this.data.map((item: AqiDataPoint): [number, number] => {
          return [DateTime.fromISO(item.timestamp.toString()).toMillis(), item.pm2_5];
        }) as any,
        color: "#06b6d4",
      },
      {
        name: "PM1.0",
        data: this.data.map((item: AqiDataPoint): [number, number] => {
          return [DateTime.fromISO(item.timestamp.toString()).toMillis(), item.pm1];
        }) as any,
        color: "#6366f1",
      },
      {
        name: "PM10",
        data: this.data.map((item: AqiDataPoint): [number, number] => {
          return [DateTime.fromISO(item.timestamp.toString()).toMillis(), item.pm10];
        }) as any,
        color: "#10b981",
      },
    ];
  }
}
